import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save, User } from 'lucide-react';
import api from '@/services/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';

export default function ModifierCollaborateur() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { secteurs, managers: contextManagers } = useApp();
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    matricule: '',
    prenom: '',
    nom: '',
    email: '',
    secteur_id: '',
    poste: '',
    manager_id: '',
    manager_nom: '',
    telephone: '',
    date_embauche: ''
  });

  useEffect(() => {
    loadCollaborateur();
    loadManagers();
  }, [id]);

  const loadManagers = async () => {
    try {
      if (contextManagers && contextManagers.length > 0) {
        setManagers(contextManagers);
      } else {
        const users = await api.getUtilisateurs();
        const mgrs = users.filter(u => u.actif !== false && u.role !== 'Collaborateur');
        setManagers(mgrs || []);
      }
    } catch (error) {
      console.error('Error loading managers:', error);
    }
  };

  const loadCollaborateur = async () => {
    try {
      const data = await api.getCollaborateur(id);
      setFormData({
        matricule: data.matricule || '',
        prenom: data.prenom || '',
        nom: data.nom || '',
        email: data.email || '',
        secteur_id: data.secteur_id || '',
        poste: data.poste || '',
        manager_id: data.manager_id || '',
        manager_nom: data.manager_nom || '',
        telephone: data.telephone || '',
        date_embauche: data.date_embauche || ''
      });
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate('/collaborateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Add manager name if manager is selected
      const selectedManager = managers.find(m => m.id === formData.manager_id);
      const dataToSubmit = {
        ...formData,
        manager_nom: selectedManager ? `${selectedManager.prenom} ${selectedManager.nom}` : ''
      };
      
      await api.updateCollaborateur(id, dataToSubmit);
      toast.success('Collaborateur mis à jour');
      navigate(`/collaborateurs/${id}`);
    } catch (error) {
      // Check if it's an access error for managers
      if (error.message && (error.message.includes('403') || error.message.includes('non autorisé') || error.message.includes('Forbidden') || error.message.includes('équipe'))) {
        toast.error('Vous ne pouvez modifier que les collaborateurs de votre équipe. Ce collaborateur n\'est pas sous votre responsabilité.');
      } else {
        toast.error(error.message || 'Erreur lors de la mise à jour');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/collaborateurs/${id}`)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Modifier le Collaborateur</h1>
          <p className="text-gray-500">{formData.prenom} {formData.nom}</p>
        </div>
      </div>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Informations du collaborateur
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="matricule">Matricule *</Label>
                <Input
                  id="matricule"
                  value={formData.matricule}
                  onChange={(e) => setFormData({ ...formData, matricule: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="prenom">Prénom *</Label>
                <Input
                  id="prenom"
                  value={formData.prenom}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="nom">Nom *</Label>
                <Input
                  id="nom"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="secteur">Secteur *</Label>
                <Select 
                  value={formData.secteur_id} 
                  onValueChange={(value) => setFormData({ ...formData, secteur_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un secteur" />
                  </SelectTrigger>
                  <SelectContent>
                    {secteurs.map((secteur) => (
                      <SelectItem key={secteur.id} value={secteur.id}>
                        {secteur.libelle}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="poste">Poste *</Label>
                <Input
                  id="poste"
                  value={formData.poste}
                  onChange={(e) => setFormData({ ...formData, poste: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="manager">Manager responsable</Label>
                <Select 
                  value={formData.manager_id || "none"} 
                  onValueChange={(value) => setFormData({ ...formData, manager_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="manager-select-edit">
                    <SelectValue placeholder="Sélectionner un responsable" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun responsable</SelectItem>
                    {managers && managers.length > 0 ? (
                      managers.map((manager) => (
                        <SelectItem key={manager.id} value={manager.id}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{manager.prenom} {manager.nom}</span>
                            <span className="text-muted-foreground text-xs px-1.5 py-0.5 bg-gray-100 rounded">
                              {manager.role}
                            </span>
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="loading" disabled>
                        Chargement des utilisateurs...
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Sélectionnez le responsable hiérarchique de ce collaborateur
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="telephone">Téléphone</Label>
                <Input
                  id="telephone"
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date_embauche">Date d'embauche</Label>
                <Input
                  id="date_embauche"
                  type="date"
                  value={formData.date_embauche}
                  onChange={(e) => setFormData({ ...formData, date_embauche: e.target.value })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-4 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => navigate(`/collaborateurs/${id}`)}>
                Annuler
              </Button>
              <Button type="submit" disabled={saving} className="gap-2">
                {saving ? (
                  <><span className="animate-spin">...</span> Enregistrement...</>
                ) : (
                  <><Save className="h-4 w-4" /> Enregistrer</> 
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
